import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CountrySelect from '../components/CountrySelect';
import RecaptchaV2, { resetRecaptcha } from '../components/RecaptchaV2';
import { useAuthConfig } from '../hooks/useAuthConfig';
import { useCountries } from '../hooks/useCountries';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company_registration_number: '',
    phone_number: '',
    country: '',
    currency: ''
  });

  const { countries, loading: fetchingCountries } = useCountries();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [autoVerified, setAutoVerified] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const { signupCompanysAdmin, resendVerificationEmail, isAuthenticated } = useAuth();
  const { recaptchaSiteKey, recaptchaEnabled, loading: configLoading } = useAuthConfig();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-set currency when country changes
    if (name === 'country') {
      const selectedCountry = countries.find((c) => String(c.id) === String(value));
      if (selectedCountry) {
        setFormData(prev => ({ ...prev, currency: selectedCountry.currency }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const { name, email, password, confirmPassword, company_registration_number, phone_number, country, currency } = formData;

    // Validation
    if (!name || !email || !password || !confirmPassword || !company_registration_number || !phone_number || !country || !currency) {
      setError('All fields are required');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (company_registration_number.length < 2) {
      setError('Please enter a valid Company registration number');
      return;
    }

    if (recaptchaEnabled && !recaptchaToken) {
      setError('Please complete the reCAPTCHA challenge');
      return;
    }

    setLoading(true);
    const result = await signupCompanysAdmin({
      name,
      email,
      password,
      company_registration_number,
      phone_number,
      country,
      currency,
      recaptchaToken,
    });
    setLoading(false);

    if (result.success) {
      setSuccess(result.message);
      setAutoVerified(!!result.autoVerified);
      setNeedsVerification(!!result.needsVerification);
      setRegisteredEmail(result.email || email);
      setResendMessage('');
      setRecaptchaToken(null);
      resetRecaptcha();
      setFormData({
        name: '', email: '', password: '', confirmPassword: '',
        company_registration_number: '', phone_number: '', country: '', currency: ''
      });
    } else {
      setError(result.error);
      setNeedsVerification(!!result.needsVerification);
      setRegisteredEmail(result.email || email);
      setRecaptchaToken(null);
      resetRecaptcha();
    }
  };

  const handleResendVerification = async () => {
    if (!registeredEmail) return;

    setResendLoading(true);
    setResendMessage('');
    const result = await resendVerificationEmail(registeredEmail);
    setResendLoading(false);

    if (result.success) {
      setResendMessage(result.message);
      setError('');
    } else {
      setResendMessage(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left - Brand Side */}
      <div className="hidden lg:flex lg:w-[45%] bg-[#f9fafb] relative overflow-hidden">
        <div className="relative z-10 flex flex-col justify-center px-16 max-w-md mx-auto">
          <div className="mb-8">
            <img src="/vughylogo.svg" alt="Vughy" className="h-8 w-auto mb-6" />
            <h1 className="text-[30px] font-semibold text-ink leading-tight mb-3 font-login">
              Register Your Company
            </h1>
            <p className="text-[#475467] text-base leading-relaxed font-login">
              Create your company admin account and start managing visa operations with Vughy.
            </p>
          </div>
        </div>
      </div>

      {/* Right - Signup Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 overflow-y-auto">
        <div className="w-full max-w-lg">
          <div className="bg-white p-8">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-6">
              <img src="/vughylogo.svg" alt="Vughy" className="h-8 w-auto mx-auto mb-3" />
              <h2 className="text-xl font-semibold text-ink">Vughy for Corporate</h2>
            </div>

            {/* Desktop Title */}
            <div className="hidden lg:block mb-8">
              <h2 className="text-[30px] font-semibold text-ink font-login">Create account</h2>
              <p className="text-[#475467] text-sm mt-1 font-login">Fill in your company details to get started</p>
            </div>

            {error && !needsVerification && (
              <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className={`${needsVerification ? 'bg-blue-50 border-blue-100 text-blue-800' : 'bg-green-50 border-green-100 text-green-700'} border px-5 py-5 rounded-xl mb-4`}>
                <div>
                  <div className="w-full">
                    <p className="text-sm font-semibold">
                      {needsVerification ? 'Check your email to activate your account' : 'Account Created Successfully!'}
                    </p>
                    <p className="text-sm mt-1">{success}</p>
                    {needsVerification && registeredEmail && (
                      <p className="text-sm mt-2">
                        Activation email sent to <span className="font-semibold">{registeredEmail}</span>.
                        Click the <strong>Activate Account</strong> button in that message before signing in.
                      </p>
                    )}
                    {resendMessage && (
                      <p className="text-sm mt-3 text-blue-700">{resendMessage}</p>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {needsVerification ? (
                        <button
                          type="button"
                          onClick={handleResendVerification}
                          disabled={resendLoading}
                          className="text-sm bg-primary text-white hover:bg-primary-hover font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {resendLoading ? 'Sending...' : 'Resend activation email'}
                        </button>
                      ) : (
                        <Link to="/login" className="text-sm bg-primary text-white hover:bg-primary-hover font-semibold px-4 py-2 rounded-lg inline-block transition-colors">
                          Go to Login
                        </Link>
                      )}
                      {autoVerified && (
                        <span className="text-xs text-green-600 self-center">Email auto-verified</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!success && needsVerification && registeredEmail && (
              <div className="bg-blue-50 border border-blue-100 text-blue-800 px-5 py-4 rounded-xl mb-4">
                <p className="text-sm font-semibold">Account activation required</p>
                <p className="text-sm mt-1">{error}</p>
                {resendMessage && <p className="text-sm mt-2">{resendMessage}</p>}
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="mt-3 text-sm bg-primary text-white hover:bg-primary-hover font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  {resendLoading ? 'Sending...' : 'Resend activation email'}
                </button>
              </div>
            )}

            {!success && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 bg-white text-sm placeholder:text-gray-400"
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 bg-white text-sm placeholder:text-gray-400"
                      placeholder="company@example.com"
                      required
                    />
                  </div>

                  {/* Company Registration Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Registration Number</label>
                    <input
                      type="text"
                      name="company_registration_number"
                      value={formData.company_registration_number}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 bg-white text-sm placeholder:text-gray-400"
                      placeholder="e.g., 12345678"
                      required
                    />
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 bg-white text-sm placeholder:text-gray-400"
                      placeholder="+1 234 567 8900"
                      required
                    />
                  </div>

                  {/* Country Dropdown */}
                  <div>
                    <CountrySelect
                      label="Country"
                      value={formData.country}
                      onChange={(next) => {
                        const selectedCountry = countries.find((c) => String(c.id) === String(next));
                        setFormData((prev) => ({
                          ...prev,
                          country: next,
                          currency: selectedCountry?.currency || '',
                        }));
                      }}
                      countries={countries}
                      loading={fetchingCountries}
                      placeholder="Select your country"
                      emptyLabel="Select your country"
                      required
                      buttonClassName="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 bg-white text-sm text-left text-gray-700 disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                  </div>

                  {/* Currency (auto-filled) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Currency</label>
                    <input
                      type="text"
                      name="currency"
                      value={formData.currency}
                      readOnly
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-sm text-gray-500 cursor-not-allowed"
                      placeholder="Select a country first"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 bg-white text-sm placeholder:text-gray-400 pr-12"
                        placeholder="Min. 6 characters"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-medium"
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 bg-white text-sm placeholder:text-gray-400 pr-12"
                        placeholder="Re-enter password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-medium"
                      >
                        {showConfirmPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                </div>

                {!configLoading && (
                  <RecaptchaV2 siteKey={recaptchaSiteKey} onChange={setRecaptchaToken} />
                )}

                <button
                  type="submit"
                  disabled={loading || fetchingCountries || configLoading}
                  className="w-full bg-primary hover:bg-primary-hover text-white font-medium py-2.5 px-6 rounded-xl transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating account...
                    </>
                  ) : (
                    'Register Company'
                  )}
                </button>
              </form>
            )}

            <div className="mt-6 text-center">
              <p className="text-gray-500 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:text-primary-hover font-semibold">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
